<template>
    <Transition name="modal-fade">
        <div v-if="show" class="modal-overlay" @click.self="closeModal">
            <div class="modal-container">
                <!-- Header -->
                <header class="modal-header">
                    <div class="header-content">
                        <div class="header-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                        </div>
                        <div>
                            <h2>Modifier le produit</h2>
                            <p class="header-subtitle">Mettez à jour les informations du produit</p>
                        </div>
                    </div>
                    <button class="btn-close" @click="closeModal" aria-label="Fermer">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </header>

                <!-- Body avec navigation par onglets -->
                <div class="modal-body">
                    <!-- Navigation tabs -->
                    <nav class="tabs-nav">
                        <button v-for="tab in tabs" :key="tab.id" class="tab-btn"
                            :class="{ active: activeTab === tab.id }" @click="activeTab = tab.id">
                            <component :is="tab.icon" />
                            <span>{{ tab.label }}</span>
                        </button>
                    </nav>

                    <form @submit.prevent="submitForm" class="form-content">
                        <!-- Tab: Informations générales -->
                        <div v-show="activeTab === 'general'" class="tab-panel">
                            <div class="panel-grid">
                                <!-- Colonne gauche: Image -->
                                <div class="image-section">
                                    <label class="label">Image du produit</label>
                                    <div class="image-uploader"
                                        :class="{ 'has-image': imagePreview || form.image_url }">
                                        <div v-if="imagePreview || form.image_url" class="image-preview">
                                            <img :src="imagePreview || form.image_url" alt="Aperçu" />
                                            <div class="image-overlay">
                                                <button type="button" class="btn-icon btn-remove" @click="removeImage">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                        <path
                                                            d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2">
                                                        </path>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                        <label v-else class="dropzone">
                                            <input type="file" @change="handleImageChange" accept="image/*" />
                                            <div class="dropzone-content">
                                                <div class="dropzone-icon">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28"
                                                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                        stroke-width="1.5" stroke-linecap="round"
                                                        stroke-linejoin="round">
                                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                                        <polyline points="21 15 16 10 5 21"></polyline>
                                                    </svg>
                                                </div>
                                                <span class="dropzone-text">Glisser ou cliquer</span>
                                                <span class="dropzone-hint">PNG, JPG (max 2 Mo)</span>
                                            </div>
                                        </label>
                                    </div>
                                    <span v-if="validationErrors.image" class="field-error">{{
                                        validationErrors.image[0] }}</span>
                                </div>

                                <!-- Colonne droite: Infos principales -->
                                <div class="info-section">
                                    <!-- Nom -->
                                    <div class="form-group">
                                        <label for="productName" class="label required">Nom du produit</label>
                                        <input id="productName" v-model="form.name" type="text"
                                            placeholder="Ex: iPhone 15 Pro Max" class="input"
                                            :class="{ error: validationErrors.name }" />
                                        <span v-if="validationErrors.name" class="field-error">{{
                                            validationErrors.name[0] }}</span>
                                    </div>

                                    <!-- Catégorie -->
                                    <div class="form-group">
                                        <label for="productCategory" class="label">Catégorie</label>
                                        <div class="select-wrapper" ref="categoryDropdownContainer">
                                            <div class="select-trigger" @click="toggleCategoryDropdown">
                                                <span :class="{ placeholder: !selectedCategoryName }">
                                                    {{ selectedCategoryName || 'Sélectionner une catégorie' }}
                                                </span>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                                    class="chevron" :class="{ open: showCategoryDropdown }">
                                                    <polyline points="6 9 12 15 18 9"></polyline>
                                                </svg>
                                            </div>
                                            <Transition name="dropdown">
                                                <div v-if="showCategoryDropdown" class="dropdown">
                                                    <div class="dropdown-search">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                            viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                            stroke-width="2" stroke-linecap="round"
                                                            stroke-linejoin="round">
                                                            <circle cx="11" cy="11" r="8"></circle>
                                                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                                        </svg>
                                                        <input v-model="searchCategory" type="text"
                                                            placeholder="Rechercher..."
                                                            @input="debouncedFetchCategories" />
                                                    </div>

                                                    <div class="dropdown-content">
                                                        <div v-if="isSearchingCategories" class="dropdown-loading">
                                                            <span class="spinner"></span>
                                                            <span>Recherche...</span>
                                                        </div>
                                                        <div v-else-if="showNewCategoryInput" class="new-category-form">
                                                            <input v-model="newCategoryName" type="text"
                                                                placeholder="Nom de la catégorie" class="input"
                                                                ref="newCategoryInput" @keyup.enter="createNewCategory"
                                                                @keyup.esc="cancelNewCategory" />
                                                            <div class="new-category-actions">
                                                                <button type="button" class="btn-icon"
                                                                    @click="cancelNewCategory"
                                                                    :disabled="isCreatingCategory">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16"
                                                                        height="16" viewBox="0 0 24 24" fill="none"
                                                                        stroke="currentColor" stroke-width="2">
                                                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                                                    </svg>
                                                                </button>
                                                                <button type="button" class="btn-icon btn-confirm"
                                                                    @click="createNewCategory"
                                                                    :disabled="!newCategoryName.trim() || isCreatingCategory">
                                                                    <span v-if="isCreatingCategory"
                                                                        class="spinner small"></span>
                                                                    <svg v-else xmlns="http://www.w3.org/2000/svg"
                                                                        width="16" height="16" viewBox="0 0 24 24"
                                                                        fill="none" stroke="currentColor"
                                                                        stroke-width="2">
                                                                        <polyline points="20 6 9 17 4 12"></polyline>
                                                                    </svg>
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <ul v-else-if="filteredCategories.length" class="dropdown-list">
                                                            <li v-for="category in filteredCategories"
                                                                :key="category.id" @click="selectCategory(category)"
                                                                :class="{ selected: form.category_id === category.id }">
                                                                <span class="category-name">{{ category.name }}</span>
                                                                <svg v-if="form.category_id === category.id"
                                                                    xmlns="http://www.w3.org/2000/svg" width="16"
                                                                    height="16" viewBox="0 0 24 24" fill="none"
                                                                    stroke="currentColor" stroke-width="2">
                                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                                </svg>
                                                            </li>
                                                        </ul>
                                                        <div v-else class="dropdown-empty">
                                                            <p>Aucune catégorie trouvée</p>
                                                        </div>
                                                    </div>

                                                    <button type="button" class="dropdown-action"
                                                        @click="prepareNewCategory">
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                            viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                            stroke-width="2">
                                                            <line x1="12" y1="5" x2="12" y2="19"></line>
                                                            <line x1="5" y1="12" x2="19" y2="12"></line>
                                                        </svg>
                                                        Créer une catégorie
                                                    </button>
                                                </div>
                                            </Transition>
                                        </div>
                                        <span v-if="validationErrors.category_id" class="field-error">
                                            {{ validationErrors.category_id[0] }}
                                        </span>
                                    </div>

                                    <!-- Code-barres -->
                                    <div class="form-group">
                                        <label for="productBarcode" class="label">Code-barres</label>
                                        <input id="productBarcode" v-model="form.barcode" type="text"
                                            placeholder="Ex: 1234567890123" maxlength="100" class="input"
                                            :class="{ error: validationErrors.barcode }" />
                                        <span v-if="validationErrors.barcode" class="field-error">{{
                                            validationErrors.barcode[0] }}</span>
                                    </div>

                                    <!-- SKU -->
                                    <div class="form-group">
                                        <label for="productSku" class="label">SKU</label>
                                        <input id="productSku" v-model="form.sku" type="text"
                                            placeholder="Ex: IPH-15-PM-256" maxlength="100" class="input"
                                            :class="{ error: validationErrors.sku }" />
                                        <span v-if="validationErrors.sku" class="field-error">{{
                                            validationErrors.sku[0] }}</span>
                                    </div>

                                    <!-- Référence -->
                                    <div class="form-group">
                                        <label for="productReference" class="label">Référence</label>
                                        <input id="productReference" v-model="form.reference" type="text"
                                            placeholder="Ex: REF-2024-001" maxlength="100" class="input"
                                            :class="{ error: validationErrors.reference }" />
                                        <span v-if="validationErrors.reference" class="field-error">{{
                                            validationErrors.reference[0] }}</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Description (pleine largeur) -->
                            <div class="form-group full-width">
                                <label for="productDescription" class="label">Description</label>
                                <textarea id="productDescription" v-model="form.description"
                                    placeholder="Décrivez votre produit..." class="input textarea" rows="3"
                                    :class="{ error: validationErrors.description }"></textarea>
                                <span v-if="validationErrors.description" class="field-error">{{
                                    validationErrors.description[0] }}</span>
                            </div>
                        </div>

                        <!-- Tab: Tarification -->
                        <div v-show="activeTab === 'pricing'" class="tab-panel">
                            <div class="pricing-cards">
                                <!-- Prix HT -->
                                <div class="pricing-card">
                                    <div class="pricing-card-header">
                                        <div class="pricing-icon">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                <line x1="12" y1="1" x2="12" y2="23"></line>
                                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                                            </svg>
                                        </div>
                                        <span class="pricing-label">Prix HT</span>
                                    </div>
                                    <div class="pricing-input-wrapper">
                                        <input id="productPriceExclTax" v-model="form.price_excl_tax" type="number"
                                            min="0" step="50" placeholder="0" class="pricing-input"
                                            :class="{ error: validationErrors.price_excl_tax }" />
                                        <span class="pricing-currency">FCFA</span>
                                    </div>
                                    <span v-if="validationErrors.price_excl_tax" class="field-error">
                                        {{ validationErrors.price_excl_tax[0] }}
                                    </span>
                                </div>

                                <!-- TVA -->
                                <div class="pricing-card">
                                    <div class="pricing-card-header">
                                        <div class="pricing-icon tva">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <line x1="14.31" y1="8" x2="20.05" y2="17.94"></line>
                                                <line x1="9.69" y1="8" x2="21.17" y2="8"></line>
                                                <line x1="7.38" y1="12" x2="13.12" y2="2.06"></line>
                                                <line x1="9.69" y1="16" x2="3.95" y2="6.06"></line>
                                                <line x1="14.31" y1="16" x2="2.83" y2="16"></line>
                                                <line x1="16.62" y1="12" x2="10.88" y2="21.94"></line>
                                            </svg>
                                        </div>
                                        <span class="pricing-label">Taux TVA</span>
                                    </div>
                                    <div class="pricing-input-wrapper">
                                        <input id="productTaxRate" v-model="form.tax_rate" type="number" min="0"
                                            max="100" step="1" placeholder="18" class="pricing-input"
                                            :class="{ error: validationErrors.tax_rate }" />
                                        <span class="pricing-currency">%</span>
                                    </div>
                                    <span v-if="validationErrors.tax_rate" class="field-error">
                                        {{ validationErrors.tax_rate[0] }}
                                    </span>
                                </div>

                                <!-- Prix TTC (calculé) -->
                                <div class="pricing-card computed">
                                    <div class="pricing-card-header">
                                        <div class="pricing-icon ttc">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                                                <line x1="1" y1="10" x2="23" y2="10"></line>
                                            </svg>
                                        </div>
                                        <span class="pricing-label">Prix TTC</span>
                                    </div>
                                    <div class="pricing-computed">
                                        <span class="computed-value">{{ computedPriceTTC }}</span>
                                        <span class="computed-currency">FCFA</span>
                                    </div>
                                    <span class="pricing-hint">Calculé automatiquement</span>
                                </div>
                            </div>
                        </div>

                        <!-- Tab: Stock -->
                        <div v-show="activeTab === 'stock'" class="tab-panel">
                            <!-- Toggle suivi inventaire -->
                            <div class="toggle-section">
                                <label class="toggle-wrapper">
                                    <input type="checkbox" v-model="form.track_inventory" class="toggle-input" />
                                    <span class="toggle-slider"></span>
                                </label>
                                <div class="toggle-content">
                                    <span class="toggle-label">Suivre l'inventaire</span>
                                    <span class="toggle-hint">Activez pour gérer les alertes de stock</span>
                                </div>
                            </div>

                            <!-- Unité -->
                            <div class="form-group">
                                <label for="productUnit" class="label">Unité de mesure</label>
                                <input id="productUnit" v-model="form.unit" type="text"
                                    placeholder="Ex: pièce, kg, litre..." maxlength="50" class="input"
                                    :class="{ error: validationErrors.unit }" />
                                <span v-if="validationErrors.unit" class="field-error">{{
                                    validationErrors.unit[0] }}</span>
                            </div>

                            <!-- Grille de stock -->
                            <div class="stock-grid">
                                <div class="stock-card">
                                    <label class="stock-label">Stock actuel</label>
                                    <div class="stock-input-wrapper">
                                        <button type="button" class="stock-btn minus"
                                            @click="adjustStock('stock_quantity', -1)">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                <line x1="5" y1="12" x2="19" y2="12"></line>
                                            </svg>
                                        </button>
                                        <input v-model.number="form.stock_quantity" type="number" min="0"
                                            class="stock-input" />
                                        <button type="button" class="stock-btn plus"
                                            @click="adjustStock('stock_quantity', 1)">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                                <line x1="5" y1="12" x2="19" y2="12"></line>
                                            </svg>
                                        </button>
                                    </div>
                                    <span v-if="validationErrors.stock_quantity" class="field-error">
                                        {{ validationErrors.stock_quantity[0] }}
                                    </span>
                                </div>

                                <Transition name="fade">
                                    <div v-if="form.track_inventory" class="stock-card warning">
                                        <label class="stock-label">Stock minimum</label>
                                        <div class="stock-input-wrapper">
                                            <button type="button" class="stock-btn minus"
                                                @click="adjustStock('minimum_stock', -1)">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    stroke-width="2">
                                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                                </svg>
                                            </button>
                                            <input v-model.number="form.minimum_stock" type="number" min="0"
                                                class="stock-input" placeholder="0" />
                                            <button type="button" class="stock-btn plus"
                                                @click="adjustStock('minimum_stock', 1)">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    stroke-width="2">
                                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                                </svg>
                                            </button>
                                        </div>
                                        <span class="stock-hint">Alerte si stock ≤ cette valeur</span>
                                    </div>
                                </Transition>

                                <Transition name="fade">
                                    <div v-if="form.track_inventory" class="stock-card success">
                                        <label class="stock-label">Stock maximum</label>
                                        <div class="stock-input-wrapper">
                                            <button type="button" class="stock-btn minus"
                                                @click="adjustStock('maximum_stock', -1)">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    stroke-width="2">
                                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                                </svg>
                                            </button>
                                            <input v-model.number="form.maximum_stock" type="number" min="0"
                                                class="stock-input" placeholder="0" />
                                            <button type="button" class="stock-btn plus"
                                                @click="adjustStock('maximum_stock', 1)">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                    stroke-width="2">
                                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                                </svg>
                                            </button>
                                        </div>
                                        <span class="stock-hint">Capacité maximale</span>
                                    </div>
                                </Transition>
                            </div>

                            <!-- Indicateur visuel du stock -->
                            <Transition name="fade">
                                <div v-if="form.track_inventory && form.maximum_stock > 0" class="stock-indicator">
                                    <div class="stock-bar">
                                        <div class="stock-fill" :style="{ width: stockPercentage + '%' }"
                                            :class="stockStatus"></div>
                                        <div v-if="form.minimum_stock" class="stock-marker"
                                            :style="{ left: minimumStockPercentage + '%' }">
                                            <span class="marker-label">Min</span>
                                        </div>
                                    </div>
                                    <div class="stock-legend">
                                        <span>{{ form.stock_quantity || 0 }} / {{ form.maximum_stock }} unités</span>
                                        <span class="stock-status-text" :class="stockStatus">{{ stockStatusText
                                            }}</span>
                                    </div>
                                </div>
                            </Transition>
                        </div>
                    </form>
                </div>

                <!-- Footer -->
                <footer class="modal-footer">
                    <div class="footer-left">
                        <span class="required-hint">* Champs obligatoires</span>
                    </div>
                    <div class="footer-actions">
                        <button type="button" class="btn btn-secondary" @click="closeModal">
                            Annuler
                        </button>
                        <button type="button" class="btn btn-primary" :disabled="isSubmitting" @click="submitForm">
                            <span v-if="isSubmitting" class="spinner"></span>
                            <template v-else>
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                                <span>Mettre à jour</span>
                            </template>
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    </Transition>
</template>

<script>
import { useToastStore } from '@/store/modules';
import { categoriesService } from '@/services';
import { ref, onMounted, onBeforeUnmount, nextTick, h } from 'vue';

// Icônes pour les onglets
const IconInfo = {
    render() {
        return h('svg', {
            xmlns: 'http://www.w3.org/2000/svg',
            width: '18',
            height: '18',
            viewBox: '0 0 24 24',
            fill: 'none',
            stroke: 'currentColor',
            'stroke-width': '2',
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round'
        }, [
            h('circle', { cx: '12', cy: '12', r: '10' }),
            h('line', { x1: '12', y1: '16', x2: '12', y2: '12' }),
            h('line', { x1: '12', y1: '8', x2: '12.01', y2: '8' })
        ]);
    }
};

const IconPricing = {
    render() {
        return h('svg', {
            xmlns: 'http://www.w3.org/2000/svg',
            width: '18',
            height: '18',
            viewBox: '0 0 24 24',
            fill: 'none',
            stroke: 'currentColor',
            'stroke-width': '2',
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round'
        }, [
            h('line', { x1: '12', y1: '1', x2: '12', y2: '23' }),
            h('path', { d: 'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' })
        ]);
    }
};

const IconStock = {
    render() {
        return h('svg', {
            xmlns: 'http://www.w3.org/2000/svg',
            width: '18',
            height: '18',
            viewBox: '0 0 24 24',
            fill: 'none',
            stroke: 'currentColor',
            'stroke-width': '2',
            'stroke-linecap': 'round',
            'stroke-linejoin': 'round'
        }, [
            h('path', { d: 'M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z' }),
            h('polyline', { points: '3.27 6.96 12 12.01 20.73 6.96' }),
            h('line', { x1: '12', y1: '22.08', x2: '12', y2: '12' })
        ]);
    }
};

export default {
    name: 'EditProductModal',
    props: {
        show: {
            type: Boolean,
            default: false
        },
        product: {
            type: Object,
            required: true
        },
        categories: {
            type: Array,
            default: () => []
        }
    },
    setup() {
        const toastStore = useToastStore();
        const categoryDropdownContainer = ref(null);
        const showNewCategoryInput = ref(false);
        const newCategoryName = ref('');
        const isCreatingCategory = ref(false);
        const showCategoryDropdown = ref(false);
        const activeTab = ref('general');

        const tabs = [
            { id: 'general', label: 'Informations', icon: IconInfo },
            { id: 'pricing', label: 'Tarification', icon: IconPricing },
            { id: 'stock', label: 'Stock', icon: IconStock }
        ];

        const handleClickOutside = (event) => {
            if (categoryDropdownContainer.value && !categoryDropdownContainer.value.contains(event.target)) {
                showCategoryDropdown.value = false;
            }
        };

        onMounted(() => {
            document.addEventListener('click', handleClickOutside);
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', handleClickOutside);
        });

        return {
            toastStore,
            categoryDropdownContainer,
            showNewCategoryInput,
            newCategoryName,
            isCreatingCategory,
            showCategoryDropdown,
            activeTab,
            tabs
        };
    },
    data() {
        return {
            form: {
                name: '',
                description: '',
                sku: '',
                barcode: '',
                reference: '',
                price_excl_tax: '',
                tax_rate: 0,
                category_id: '',
                stock_quantity: 0,
                minimum_stock: null,
                maximum_stock: null,
                unit: '',
                track_inventory: true,
                image: null,
                image_url: null
            },
            imagePreview: null,
            validationErrors: {},
            isSubmitting: false,
            availableCategories: [],
            searchCategory: '',
            isSearchingCategories: false,
            selectedCategoryName: '',
            fetchCategoriesTimeout: null
        };
    },
    computed: {
        filteredCategories() {
            if (!this.searchCategory.trim()) {
                return this.availableCategories;
            }
            const searchTerm = this.searchCategory.toLowerCase().trim();
            return this.availableCategories.filter(
                category => category.name.toLowerCase().includes(searchTerm)
            );
        },
        computedPriceTTC() {
            const priceHT = parseFloat(this.form.price_excl_tax) || 0;
            const taxRate = parseFloat(this.form.tax_rate) || 0;
            const priceTTC = priceHT * (1 + taxRate / 100);
            return new Intl.NumberFormat('fr-FR').format(Math.round(priceTTC));
        },
        stockPercentage() {
            if (!this.form.maximum_stock) return 0;
            return Math.min(100, (this.form.stock_quantity / this.form.maximum_stock) * 100);
        },
        minimumStockPercentage() {
            if (!this.form.maximum_stock || !this.form.minimum_stock) return 0;
            return (this.form.minimum_stock / this.form.maximum_stock) * 100;
        },
        stockStatus() {
            if (!this.form.minimum_stock) return 'normal';
            if (this.form.stock_quantity <= this.form.minimum_stock) return 'critical';
            if (this.form.stock_quantity <= this.form.minimum_stock * 1.5) return 'warning';
            return 'good';
        },
        stockStatusText() {
            switch (this.stockStatus) {
                case 'critical': return 'Stock critique';
                case 'warning': return 'Stock bas';
                case 'good': return 'Stock optimal';
                default: return '';
            }
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.initForm();
                this.fetchCategories();
                this.activeTab = 'general';
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        },
        product: {
            handler(newVal) {
                if (this.show && newVal) {
                    this.initForm();
                }
            },
            deep: true
        }
    },
    methods: {
        initForm() {
            if (!this.product) return;

            // Copier les propriétés du produit existant
            this.form = {
                name: this.product.name || '',
                description: this.product.description || '',
                sku: this.product.sku || '',
                barcode: this.product.barcode || '',
                reference: this.product.reference || '',
                price_excl_tax: this.product.price_excl_tax || '',
                tax_rate: this.product.tax_rate || 0,
                category_id: this.product.category_id || '',
                stock_quantity: this.product.stock_quantity || 0,
                minimum_stock: this.product.minimum_stock || null,
                maximum_stock: this.product.maximum_stock || null,
                unit: this.product.unit || '',
                track_inventory: this.product.track_inventory !== undefined ? this.product.track_inventory : true,
                image: null,
                image_url: this.product.image_url || null
            };

            // Si une catégorie est sélectionnée
            if (this.form.category_id && this.categories.length > 0) {
                const category = this.categories.find(c => c.id === this.form.category_id);
                if (category) {
                    this.selectedCategoryName = category.name;
                    this.searchCategory = category.name;
                }
            } else {
                this.loadSelectedCategoryDetails();
            }

            // Pré-remplir l'aperçu de l'image existante
            this.imagePreview = this.product.image_url || null;
            this.validationErrors = {};
        },

        async loadSelectedCategoryDetails() {
            if (this.form.category_id) {
                try {
                    const response = await categoriesService.getCategory(this.form.category_id);
                    if (response && response.data) {
                        this.selectedCategoryName = response.data.name;
                        this.searchCategory = response.data.name;
                    }
                } catch (error) {
                    console.error('Erreur lors du chargement des détails de la catégorie:', error);
                }
            }
        },

        toggleCategoryDropdown() {
            this.showCategoryDropdown = !this.showCategoryDropdown;
        },
        debouncedFetchCategories() {
            clearTimeout(this.fetchCategoriesTimeout);
            this.fetchCategoriesTimeout = setTimeout(() => {
                this.fetchCategories();
            }, 300);
        },
        prepareNewCategory() {
            this.showNewCategoryInput = true;
            this.newCategoryName = this.searchCategory;
            nextTick(() => {
                if (this.$refs.newCategoryInput) {
                    this.$refs.newCategoryInput.focus();
                }
            });
        },
        cancelNewCategory() {
            this.showNewCategoryInput = false;
            this.newCategoryName = '';
        },
        async createNewCategory() {
            if (!this.newCategoryName.trim()) return;
            this.isCreatingCategory = true;
            try {
                const response = await categoriesService.createCategory({
                    name: this.newCategoryName.trim()
                });
                const newCategory = response.data.category || response.data;
                this.availableCategories.push(newCategory);
                this.form.category_id = newCategory.id;
                this.selectedCategoryName = newCategory.name;
                this.searchCategory = newCategory.name;
                this.toastStore.addToast({
                    type: 'success',
                    message: 'Catégorie créée avec succès!'
                });
                this.showNewCategoryInput = false;
                this.newCategoryName = '';
                this.showCategoryDropdown = false;
            } catch (error) {
                console.error('Erreur lors de la création de la catégorie:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors de la création de la catégorie.',
                    duration: 5000
                });
            } finally {
                this.isCreatingCategory = false;
            }
        },
        async fetchCategories() {
            this.isSearchingCategories = true;
            try {
                const response = await categoriesService.getCategories({
                    perPage: 20,
                    page: 1,
                    search: this.searchCategory
                });
                if (response && response.data) {
                    this.availableCategories = response.data.data || response.data;
                }
            } catch (error) {
                console.error('Erreur lors de la récupération des catégories:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors de la récupération des catégories.',
                    duration: 6000
                });
            } finally {
                this.isSearchingCategories = false;
            }
        },
        selectCategory(category) {
            this.form.category_id = category.id;
            this.selectedCategoryName = category.name;
            this.searchCategory = category.name;
            this.showCategoryDropdown = false;
        },
        handleImageChange(event) {
            const file = event.target.files[0];
            if (!file) return;
            if (file.size > 2 * 1024 * 1024) {
                this.validationErrors.image = ['L\'image ne doit pas dépasser 2 Mo'];
                return;
            }
            this.form.image = file;
            this.validationErrors.image = null;
            const reader = new FileReader();
            reader.onload = (e) => {
                this.imagePreview = e.target.result;
            };
            reader.readAsDataURL(file);
        },
        removeImage() {
            this.form.image = null;
            this.form.image_url = null;
            this.imagePreview = null;
        },
        adjustStock(field, delta) {
            const current = this.form[field] || 0;
            this.form[field] = Math.max(0, current + delta);
        },
        validateForm() {
            let isValid = true;
            this.validationErrors = {};

            if (!this.form.name) {
                this.validationErrors.name = ['Le nom du produit est requis'];
                isValid = false;
            } else if (this.form.name.length > 255) {
                this.validationErrors.name = ['Le nom ne doit pas dépasser 255 caractères'];
                isValid = false;
            }

            if (!this.form.price_excl_tax) {
                this.validationErrors.price_excl_tax = ['Le prix HT est requis'];
                isValid = false;
            } else if (isNaN(parseFloat(this.form.price_excl_tax)) || parseFloat(this.form.price_excl_tax) < 0) {
                this.validationErrors.price_excl_tax = ['Le prix doit être un nombre positif'];
                isValid = false;
            }

            if (this.form.tax_rate === '' || this.form.tax_rate === null) {
                this.validationErrors.tax_rate = ['Le taux de TVA est requis'];
                isValid = false;
            } else if (
                isNaN(parseFloat(this.form.tax_rate)) ||
                parseFloat(this.form.tax_rate) < 0 ||
                parseFloat(this.form.tax_rate) > 100
            ) {
                this.validationErrors.tax_rate = ['Le taux de TVA doit être entre 0 et 100'];
                isValid = false;
            }

            return isValid;
        },
        submitForm() {
            if (!this.validateForm()) {
                // Naviguer vers l'onglet avec erreur
                if (this.validationErrors.name || this.validationErrors.category_id) {
                    this.activeTab = 'general';
                } else if (this.validationErrors.price_excl_tax || this.validationErrors.tax_rate) {
                    this.activeTab = 'pricing';
                }
                return;
            }

            this.isSubmitting = true;
            const formData = { ...this.form };

            if (!formData.sku?.trim()) formData.sku = null;
            if (!formData.barcode?.trim()) formData.barcode = null;
            if (!formData.reference?.trim()) formData.reference = null;
            if (!formData.description?.trim()) formData.description = null;
            if (!formData.unit?.trim()) formData.unit = null;
            if (!formData.category_id) formData.category_id = null;
            if (formData.minimum_stock === null || formData.minimum_stock === '') formData.minimum_stock = null;
            if (formData.maximum_stock === null || formData.maximum_stock === '') formData.maximum_stock = null;
            if (formData.stock_quantity === null || formData.stock_quantity === '') formData.stock_quantity = null;
            formData.track_inventory = Boolean(formData.track_inventory);

            this.$emit('submit', formData);
        },
        closeModal() {
            this.$emit('close');
        }
    }
};
</script>

<style>
/* ========================================
   Variables CSS (Non-scoped)
   ======================================== */
.modal-overlay {
    --primary: #0A5C6B;
    --primary-dark: #084550;
    --primary-light: #e0f4f7;
    --secondary: #64748b;
    --success: #10b981;
    --warning: #f59e0b;
    --danger: #ef4444;
    --gray-50: #f8fafc;
    --gray-100: #f1f5f9;
    --gray-200: #e2e8f0;
    --gray-300: #cbd5e1;
    --gray-400: #94a3b8;
    --gray-500: #64748b;
    --gray-600: #475569;
    --gray-700: #334155;
    --gray-800: #1e293b;
    --gray-900: #0f172a;
    --radius-sm: 6px;
    --radius: 10px;
    --radius-lg: 14px;
    --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
    --shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    --shadow-lg: 0 12px 32px rgba(0, 0, 0, 0.12);
}
</style>

<style scoped>
/* ========================================
   Modal Overlay & Container
   ======================================== */
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 24px;
}

.modal-container {
    width: 100%;
    max-width: 780px;
    max-height: calc(100vh - 48px);
    background: white;
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-lg);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* ========================================
   Header
   ======================================== */
.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid var(--gray-200);
    background: linear-gradient(135deg, var(--gray-50) 0%, white 100%);
}

.header-content {
    display: flex;
    align-items: center;
    gap: 14px;
}

.header-icon {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
    border-radius: var(--radius);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.modal-header h2 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--gray-900);
    letter-spacing: -0.02em;
}

.header-subtitle {
    margin: 2px 0 0;
    font-size: 0.875rem;
    color: var(--gray-500);
}

.btn-close {
    width: 36px;
    height: 36px;
    border: none;
    background: var(--gray-100);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--gray-500);
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-close:hover {
    background: var(--gray-200);
    color: var(--gray-700);
    transform: rotate(90deg);
}

/* ========================================
   Tabs Navigation
   ======================================== */
.tabs-nav {
    display: flex;
    gap: 4px;
    padding: 16px 24px 0;
    border-bottom: 1px solid var(--gray-200);
    background: var(--gray-50);
}

.tab-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    border: none;
    background: transparent;
    color: var(--gray-500);
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    border-radius: var(--radius) var(--radius) 0 0;
    transition: all 0.2s ease;
    position: relative;
}

.tab-btn::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: transparent;
    transition: background 0.2s ease;
}

.tab-btn:hover {
    color: var(--primary);
    background: rgba(10, 92, 107, 0.05);
}

.tab-btn.active {
    color: var(--primary);
    background: white;
}

.tab-btn.active::after {
    background: var(--primary);
}

/* ========================================
   Modal Body
   ======================================== */
.modal-body {
    flex: 1;
    overflow-y: auto;
    background: white;
}

.form-content {
    padding: 24px;
}

.tab-panel {
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(8px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* ========================================
   Panel Grid (General Tab)
   ======================================== */
.panel-grid {
    display: grid;
    grid-template-columns: 200px 1fr;
    gap: 24px;
    margin-bottom: 20px;
}

@media (max-width: 640px) {
    .panel-grid {
        grid-template-columns: 1fr;
    }
}

/* ========================================
   Form Elements
   ======================================== */
.form-group {
    margin-bottom: 18px;
}

.form-group.full-width {
    margin-top: 4px;
}

.label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--gray-700);
    margin-bottom: 8px;
}

.label.required::after {
    content: '*';
    color: var(--danger);
    margin-left: 4px;
}

.input {
    width: 100%;
    padding: 11px 14px;
    font-size: 0.95rem;
    color: var(--gray-800);
    background: var(--gray-50);
    border: 1.5px solid var(--gray-200);
    border-radius: var(--radius);
    transition: all 0.2s ease;
}

.input:focus {
    outline: none;
    background: white;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.input::placeholder {
    color: var(--gray-400);
}

.input.error {
    border-color: var(--danger);
    background: #fef2f2;
}

.input.textarea {
    resize: vertical;
    min-height: 80px;
}

.field-error {
    display: block;
    font-size: 0.8rem;
    color: var(--danger);
    margin-top: 6px;
}

/* ========================================
   Image Uploader
   ======================================== */
.image-section .label {
    margin-bottom: 10px;
}

.image-uploader {
    width: 100%;
    aspect-ratio: 1;
    border: 2px dashed var(--gray-300);
    border-radius: var(--radius);
    overflow: hidden;
    transition: all 0.2s ease;
    background: var(--gray-50);
}

.image-uploader:hover {
    border-color: var(--primary);
    background: var(--primary-light);
}

.image-uploader.has-image {
    border-style: solid;
    border-color: var(--gray-200);
}

.dropzone {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.dropzone input {
    display: none;
}

.dropzone-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    text-align: center;
    padding: 16px;
}

.dropzone-icon {
    width: 52px;
    height: 52px;
    background: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary);
    box-shadow: var(--shadow);
}

.dropzone-text {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--gray-700);
}

.dropzone-hint {
    font-size: 0.8rem;
    color: var(--gray-400);
}

.image-preview {
    width: 100%;
    height: 100%;
    position: relative;
}

.image-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.image-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.image-preview:hover .image-overlay {
    opacity: 1;
}

.btn-remove {
    background: white;
    color: var(--danger);
}

.btn-remove:hover {
    background: var(--danger);
    color: white;
}

/* ========================================
   Select / Dropdown
   ======================================== */
.select-wrapper {
    position: relative;
}

.select-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 11px 14px;
    background: var(--gray-50);
    border: 1.5px solid var(--gray-200);
    border-radius: var(--radius);
    cursor: pointer;
    transition: all 0.2s ease;
}

.select-trigger:hover {
    border-color: var(--gray-300);
}

.select-trigger .placeholder {
    color: var(--gray-400);
}

.select-trigger .chevron {
    color: var(--gray-400);
    transition: transform 0.2s ease;
}

.select-trigger .chevron.open {
    transform: rotate(180deg);
}

.dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    background: white;
    border: 1px solid var(--gray-200);
    border-radius: var(--radius);
    box-shadow: var(--shadow-lg);
    z-index: 50;
    overflow: hidden;
}

.dropdown-search {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid var(--gray-100);
    color: var(--gray-400);
}

.dropdown-search input {
    flex: 1;
    border: none;
    background: none;
    font-size: 0.9rem;
    color: var(--gray-800);
    outline: none;
}

.dropdown-content {
    max-height: 200px;
    overflow-y: auto;
}

.dropdown-list {
    list-style: none;
    margin: 0;
    padding: 6px;
}

.dropdown-list li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    font-size: 0.9rem;
    color: var(--gray-700);
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: background 0.15s ease;
}

.dropdown-list li:hover {
    background: var(--gray-50);
}

.dropdown-list li.selected {
    background: var(--primary-light);
    color: var(--primary);
    font-weight: 500;
}

.dropdown-empty,
.dropdown-loading {
    padding: 20px;
    text-align: center;
    color: var(--gray-500);
    font-size: 0.9rem;
}

.dropdown-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
}

.dropdown-action {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 12px;
    border: none;
    border-top: 1px solid var(--gray-100);
    background: var(--gray-50);
    color: var(--primary);
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;
}

.dropdown-action:hover {
    background: var(--primary-light);
}

.new-category-form {
    padding: 12px;
    display: flex;
    gap: 8px;
}

.new-category-form .input {
    flex: 1;
}

.new-category-actions {
    display: flex;
    gap: 6px;
}

/* ========================================
   Pricing Cards
   ======================================== */
.pricing-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

@media (max-width: 640px) {
    .pricing-cards {
        grid-template-columns: 1fr;
    }
}

.pricing-card {
    background: var(--gray-50);
    border: 1.5px solid var(--gray-200);
    border-radius: var(--radius);
    padding: 20px;
    transition: all 0.2s ease;
}

.pricing-card:hover {
    border-color: var(--primary);
    box-shadow: 0 4px 12px rgba(10, 92, 107, 0.08);
}

.pricing-card.computed {
    background: linear-gradient(135deg, var(--primary-light) 0%, #d1fae5 100%);
    border-color: transparent;
}

.pricing-card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
}

.pricing-icon {
    width: 36px;
    height: 36px;
    background: white;
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary);
    box-shadow: var(--shadow-sm);
}

.pricing-icon.tva {
    color: #8b5cf6;
}

.pricing-icon.ttc {
    color: var(--success);
}

.pricing-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--gray-600);
}

.pricing-input-wrapper {
    display: flex;
    align-items: center;
    background: white;
    border: 1.5px solid var(--gray-200);
    border-radius: var(--radius);
    overflow: hidden;
    transition: border-color 0.2s ease;
}

.pricing-input-wrapper:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.pricing-input {
    flex: 1;
    padding: 12px 14px;
    border: none;
    background: none;
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--gray-800);
    outline: none;
}

.pricing-input::placeholder {
    color: var(--gray-300);
    font-weight: 400;
}

.pricing-currency {
    padding: 12px 14px;
    background: var(--gray-50);
    color: var(--gray-500);
    font-size: 0.875rem;
    font-weight: 500;
}

.pricing-computed {
    display: flex;
    align-items: baseline;
    gap: 6px;
}

.computed-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--gray-900);
}

.computed-currency {
    font-size: 0.9rem;
    color: var(--gray-500);
    font-weight: 500;
}

.pricing-hint {
    display: block;
    font-size: 0.8rem;
    color: var(--gray-500);
    margin-top: 8px;
}

/* ========================================
   Stock Section
   ======================================== */
.toggle-section {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 20px;
    background: var(--gray-50);
    border-radius: var(--radius);
    margin-bottom: 24px;
}

.toggle-wrapper {
    position: relative;
    display: inline-block;
    width: 48px;
    height: 26px;
    flex-shrink: 0;
}

.toggle-input {
    opacity: 0;
    width: 0;
    height: 0;
}

.toggle-slider {
    position: absolute;
    cursor: pointer;
    inset: 0;
    background: var(--gray-300);
    border-radius: 26px;
    transition: all 0.3s ease;
}

.toggle-slider::before {
    content: '';
    position: absolute;
    height: 20px;
    width: 20px;
    left: 3px;
    bottom: 3px;
    background: white;
    border-radius: 50%;
    transition: all 0.3s ease;
    box-shadow: var(--shadow-sm);
}

.toggle-input:checked+.toggle-slider {
    background: var(--primary);
}

.toggle-input:checked+.toggle-slider::before {
    transform: translateX(22px);
}

.toggle-content {
    display: flex;
    flex-direction: column;
}

.toggle-label {
    font-size: 0.95rem;
    font-weight: 500;
    color: var(--gray-800);
}

.toggle-hint {
    font-size: 0.8rem;
    color: var(--gray-500);
    margin-top: 2px;
}

.stock-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 24px;
}

@media (max-width: 640px) {
    .stock-grid {
        grid-template-columns: 1fr;
    }
}

.stock-card {
    background: white;
    border: 1.5px solid var(--gray-200);
    border-radius: var(--radius);
    padding: 18px;
}

.stock-card.warning {
    border-color: #fcd34d;
    background: #fffbeb;
}

.stock-card.success {
    border-color: #86efac;
    background: #f0fdf4;
}

.stock-label {
    display: block;
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--gray-600);
    margin-bottom: 12px;
}

.stock-input-wrapper {
    display: flex;
    align-items: center;
    background: var(--gray-50);
    border: 1.5px solid var(--gray-200);
    border-radius: var(--radius);
    overflow: hidden;
}

.stock-btn {
    width: 40px;
    height: 44px;
    border: none;
    background: transparent;
    color: var(--gray-500);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
}

.stock-btn:hover {
    background: var(--gray-100);
    color: var(--gray-700);
}

.stock-btn.plus:hover {
    color: var(--primary);
}

.stock-btn.minus:hover {
    color: var(--danger);
}

.stock-input {
    flex: 1;
    padding: 10px 8px;
    border: none;
    background: white;
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--gray-800);
    text-align: center;
    outline: none;
    -moz-appearance: textfield;
}

.stock-input::-webkit-outer-spin-button,
.stock-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.stock-hint {
    display: block;
    font-size: 0.75rem;
    color: var(--gray-500);
    margin-top: 8px;
}

/* Stock Indicator */
.stock-indicator {
    background: var(--gray-50);
    border-radius: var(--radius);
    padding: 18px;
}

.stock-bar {
    height: 10px;
    background: var(--gray-200);
    border-radius: 10px;
    overflow: visible;
    position: relative;
}

.stock-fill {
    height: 100%;
    border-radius: 10px;
    transition: width 0.4s ease, background 0.3s ease;
}

.stock-fill.normal,
.stock-fill.good {
    background: linear-gradient(90deg, var(--primary) 0%, #14b8a6 100%);
}

.stock-fill.warning {
    background: linear-gradient(90deg, var(--warning) 0%, #fbbf24 100%);
}

.stock-fill.critical {
    background: linear-gradient(90deg, var(--danger) 0%, #f87171 100%);
}

.stock-marker {
    position: absolute;
    top: -6px;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
}

.stock-marker::before {
    content: '';
    width: 3px;
    height: 22px;
    background: var(--warning);
    border-radius: 3px;
}

.marker-label {
    font-size: 0.65rem;
    font-weight: 600;
    color: var(--warning);
    margin-top: 4px;
    text-transform: uppercase;
}

.stock-legend {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;
    font-size: 0.85rem;
    color: var(--gray-600);
}

.stock-status-text {
    font-weight: 500;
}

.stock-status-text.good {
    color: var(--success);
}

.stock-status-text.warning {
    color: var(--warning);
}

.stock-status-text.critical {
    color: var(--danger);
}

/* ========================================
   Footer
   ======================================== */
.modal-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 24px;
    border-top: 1px solid var(--gray-200);
    background: var(--gray-50);
}

.required-hint {
    font-size: 0.8rem;
    color: var(--gray-500);
}

.footer-actions {
    display: flex;
    gap: 12px;
}

.btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 24px;
    font-size: 0.95rem;
    font-weight: 500;
    border-radius: var(--radius);
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-secondary {
    background: white;
    border: 1.5px solid var(--gray-300);
    color: var(--gray-700);
}

.btn-secondary:hover {
    background: var(--gray-50);
    border-color: var(--gray-400);
}

.btn-primary {
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
    border: none;
    color: white;
    box-shadow: 0 4px 12px rgba(10, 92, 107, 0.3);
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(10, 92, 107, 0.35);
}

.btn-primary:active:not(:disabled) {
    transform: translateY(0);
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* ========================================
   Icons & Utilities
   ======================================== */
.btn-icon {
    width: 36px;
    height: 36px;
    border: none;
    background: var(--gray-100);
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--gray-600);
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-icon:hover:not(:disabled) {
    background: var(--gray-200);
}

.btn-icon.btn-confirm {
    background: var(--primary);
    color: white;
}

.btn-icon.btn-confirm:hover:not(:disabled) {
    background: var(--primary-dark);
}

.btn-icon:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

.spinner.small {
    width: 14px;
    height: 14px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* ========================================
   Transitions
   ======================================== */
.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 0.25s ease;
}

.modal-fade-enter-active .modal-container,
.modal-fade-leave-active .modal-container {
    transition: transform 0.25s ease, opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}

.modal-fade-enter-from .modal-container,
.modal-fade-leave-to .modal-container {
    transform: scale(0.95) translateY(20px);
    opacity: 0;
}

.dropdown-enter-active,
.dropdown-leave-active {
    transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

.fade-enter-active,
.fade-leave-active {
    transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
</style>
